import { ref, computed, watchEffect, nextTick } from 'vue'
import { api } from '@/helpers/api'
import { useAuth } from './useAuth'
import { useGoogleAPI } from '@/composables/utils/useGoogleAPI'
import { useMediaUploader } from '@/composables/ui/useMediaUploader'
import { usePopup } from '@/composables/ui/usePopup'
import useNotification from '@/composables/useNotification.js'
import { useValidation } from '@/composables/utils/useValidation'
import useKeywords from '@/composables/useKeywords.js'

const URLS = {
    xhr: {
        mutate: {
            info: '/api/profile/v1/info/venue/create'
        }
    }
}
const venueTypes = ref([
    {
        name: 'Bar',
        val: 1,
        selected: false
    },
    {
        name: 'Coffee Shop',
        val: 2,
        selected: false
    },
    {
        name: 'Music Venue',
        val: 3,
        selected: false
    },
    {
        name: 'Nightclub',
        val: 4,
        selected: false
    },
    {
        name: 'Restaurant',
        val: 5,
        selected: false
    },
    {
        name: 'Brewery',
        val: 7,
        selected: false
    },
    {
        name: 'Winery',
        val: 8,
        selected: false
    },
    {
        name: 'Corporate',
        val: 9,
        selected: false
    },
    {
        name: 'Event Space',
        val: 10,
        selected: false
    },
    {
        name: 'Gallery',
        val: 11,
        selected: false
    },
    {
        name: 'Private Venue',
        val: 12,
        selected: false
    }
])

const firstFormValues = ref({
    venue_name: '',
    address: '',
    website: '',
    lat: 0,
    lng: 0
})

const secondFormValues = ref({
    venue_name: {
        wrapper: 'venue-name-wrapper',
        label: 'Venue Name',
        value: '',
        validate: true,
        edit: false
    },
    venue_type: {
        wrapper: 'venue-type-wrapper',
        label: 'Business Type',
        value: '',
        validate: false,
        edit: false
    },
    address: {
        wrapper: 'venue-address-wrapper',
        label: 'Address',
        value: '',
        validate: true,
        edit: false
    },
    web_site: {
        wrapper: 'venue-website-wrapper',
        label: 'Website',
        value: '',
        validate: false,
        edit: false
    }
})

const hideSocialLinks = ref(false)

const autocomplete = ref(null)

export const useVenueRegister = () => {
    const { isValid } = useValidation()
    const { persistKeywords, keywordInput } = useKeywords()
    const { notification } = useNotification()
    const { medias, updateProfileMedia, updateProfileAvatar } = useMediaUploader()
    const { placesTriggered, apiLoaded, initialize, lat, lng, fullAddress, business, website } = useGoogleAPI()
    const { VENUE_INFO_DATA, load, userValues, fetchUserInfo, updateProfileOrEventSteps } = useAuth()
    const { popup: dropdown, popupOpen: dropdownOpen, togglePopup: toggleSelectDropdown } = usePopup()

    const allVenueRequiredFieldsFilled = computed(
        () =>
            firstFormValues.value.venue_name &&
            firstFormValues.value.address &&
            firstFormValues.value.lat &&
            firstFormValues.value.lng
    )

    ;(() => {
        _populateFirstFormValues()
        _populateSecondFormValues()
    })()

    watchEffect(() => {
        const googleRef = autocomplete.value
        const ref = Array.isArray(googleRef) ? googleRef[0] : googleRef

        if (apiLoaded.value && ref) {
            initialize({
                mode: 'autocomplete',
                templateRef: ref
            })
        }
    })

    watchEffect(() => {
        if (placesTriggered.value) {
            firstFormValues.value.address = fullAddress.value
            firstFormValues.value.lng = lng.value
            firstFormValues.value.lat = lat.value
            firstFormValues.value.website = website.value.split('#')[0]
            firstFormValues.value.venue_name = business.value
            secondFormValues.value.address.value = fullAddress.value
            secondFormValues.value.web_site.value = website.value.split('#')[0]
        }
    })

    function handleSelectType(type, field) {
        venueTypes.value.forEach((type) => (type.selected = false))
        type.selected = true

        secondFormValues.value.venue_type.value = venueTypes.value.find((type) => type.selected).val
        field.edit = false
        dropdownOpen.value = false
    }
    function handleFirstFormContinue(v) {
        if (isValid(v)) {
            _populateUserValues()
            _populateSecondFormValues()
            updateProfileOrEventSteps('venue-register-two', null, true)
        } else {
            notification('error', 'Incorrect required form fields.')
        }
    }
    function handleSecondFormContinue(v) {
        if (isValid(v)) {
            _populateUserValues()
            updateProfileOrEventSteps('venue-register-three', null, true)
        } else {
            notification('error', 'Incorrect required form fields.')
        }
    }
    function handleSecondFormEdit(key, val) {
        if (val.validate && !secondFormValues.value[key].value) {
            return
        } else if (!val.validate) {
            val.edit = false
        } else if (val.validate && secondFormValues.value[key].value) {
            val.edit = false
            switch (key) {
                case 'venue_name':
                    userValues.value.venueName = val.value
                    break
                case 'web_site':
                    userValues.value.website = val.value
                    break
                case 'address':
                    userValues.value.address = val.value
                    userValues.value.lat = lat.value
                    userValues.value.lng = lng.value
                    break
            }
        }
    }
    async function handleRegisterVenue(nextStep = true) {
        _populateUserValues()

        const info = _prepVenueInfo()

        await _updateVenueMedia()
        await _updateVenueInfo(info)
        await fetchUserInfo()

        if (nextStep) {
            updateProfileOrEventSteps('venue-finalize-reg', null, true)
        }
    }
    function handleFirstFormBack() {
        _populateUserValues()
        updateProfileOrEventSteps('profile-register', 'artist-register-one')
    }
    function handleSecondFormBack() {
        _populateUserValues()
        _checkAndHideSocialLinks()
        updateProfileOrEventSteps('venue-register-one', null, true)
    }
    function handleThirdFormBack() {
        _populateUserValues()
        _checkAndHideSocialLinks()
        updateProfileOrEventSteps('venue-register-two', null, true)
    }
    function handleToggleDropdown(e) {
        toggleSelectDropdown(e)
    }
    function editVenueInfo(e, { key, val }) {
        if (Object.values(secondFormValues.value).some((v) => v.edit)) return

        switch (key) {
            case 'venue_type':
                val.edit = true
                handleToggleDropdown(e)
                break
            case 'address':
                fullAddress.value = ''
                val.edit = true
                break
            default:
                val.edit = true
                break
        }
    }
    function _checkAndHideSocialLinks() {
        if (!userValues.value.address || !userValues.value.lat || !userValues.value.lng) {
            hideSocialLinks.value = true
        }
    }
    // TODO: REFACTOR
    function _populateUserValues() {
        userValues.value.venueName = firstFormValues.value.venue_name
        userValues.value.address = firstFormValues.value.address
        userValues.value.lat = firstFormValues.value.lat
        userValues.value.lng = firstFormValues.value.lng
        userValues.value.website = firstFormValues.value.website

        userValues.value.venueName = secondFormValues.value.venue_name.value
            ? secondFormValues.value.venue_name.value
            : userValues.value.venueName
        userValues.value.address = secondFormValues.value.address.value
            ? secondFormValues.value.address.value
            : userValues.value.address
        userValues.value.website = secondFormValues.value.web_site.value
            ? secondFormValues.value.web_site.value
            : userValues.value.website
        userValues.value.venueType = secondFormValues.value.venue_type.value
        userValues.value.medias = medias.value
        if (persistKeywords.value.length) {
            userValues.value.keywords = persistKeywords.value.join(',')
        }
    }
    function _populateFirstFormValues() {
        nextTick(() => {
            firstFormValues.value.venue_name = userValues.value.venueName
            firstFormValues.value.address = userValues.value.address
            firstFormValues.value.lat = userValues.value.lat
            firstFormValues.value.lng = userValues.value.lng
            firstFormValues.value.website = userValues.value.website
        })
    }
    function _populateSecondFormValues() {
        nextTick(() => {
            secondFormValues.value.venue_name.value = userValues.value.venueName
            secondFormValues.value.venue_type.value = venueTypes.value.find((type) => type.selected)?.val
            secondFormValues.value.address.value = userValues.value.address
            secondFormValues.value.web_site.value = userValues.value.website
        })
    }
    function _prepVenueInfo() {
        let info = { venue_email: userValues.value.email }
        for (const [key, val] of Object.entries(VENUE_INFO_DATA)) {
            info = {
                ...info,
                [val]: userValues.value[key]
            }
        }
        info = _includeAffiliateID(info)

        const isCurrentTagValid = /^[ &a-zA-Z0-9-_,;]+$/.test(keywordInput.value)
        if (isCurrentTagValid) {
            const keywords = [...info.keyword.split(','), keywordInput.value.split(/[,;]/)].filter((v) => v).join(',')

            info.keyword = keywords
        }

        return info
    }
    function _includeAffiliateID(info) {
        let params

        try {
            params = new URL(window.location.toString()).searchParams
        } catch {
            return info
        }

        const affiliate = JSON.parse(params.get('affiliate')) ?? localStorage.getItem('affiliate')

        localStorage.removeItem('affiliate')

        if (affiliate) info['affiliate_code'] = affiliate.toString()

        return info
    }
    async function _updateVenueInfo(info) {
        try {
            load.value = true
            const data = await api({
                method: 'post',
                url: `${URLS.xhr.mutate.info}/${userValues.value.id}`,
                data: {
                    _method: 'POST',
                    ...info
                }
            })
            return data
        } finally {
            load.value = false
        }
    }
    async function _updateVenueMedia() {
        try {
            load.value = true
            await updateProfileAvatar('venue')
            await updateProfileMedia(medias.value, 'venue', { avatarUpload: false })
        } finally {
            load.value = false
        }
    }
    return {
        dropdown,
        dropdownOpen,
        venueTypes,
        hideSocialLinks,
        firstFormValues,
        secondFormValues,
        autocomplete,
        allVenueRequiredFieldsFilled,

        editVenueInfo,
        handleToggleDropdown,
        handleSelectType,
        handleFirstFormContinue,
        handleFirstFormBack,
        handleSecondFormContinue,
        handleSecondFormBack,
        handleSecondFormEdit,
        handleRegisterVenue,
        handleThirdFormBack
    }
}
